import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface League {
  id: number;
  name: string;
  description: string;
  venue: string;
  city: string;
  country: string;
  teamFormat: string;
  startDate: string;
  endDate: string;
  admin_id: number;
  isActive: boolean;
}

interface PointSystem {
  points_per_game_win: number;
  points_per_series_win: number;
  points_per_both: boolean;
  custom_formula?: string;
}

interface Rules {
  description: string;
}

interface LeagueDetailsEditorProps {
  league: League;
  onClose: () => void;
  onSave: (updatedLeague: League) => void;
}

const LeagueDetailsEditor: React.FC<LeagueDetailsEditorProps> = ({ league, onClose, onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<League>(league);
  const [originalFormData] = useState<League>(league); // Store original for comparison
  const [pointSystem, setPointSystem] = useState<PointSystem>({
    points_per_game_win: 1,
    points_per_series_win: 1,
    points_per_both: false,
    custom_formula: ''
  });
  const [originalPointSystem, setOriginalPointSystem] = useState<PointSystem>({
    points_per_game_win: 1,
    points_per_series_win: 1,
    points_per_both: false,
    custom_formula: ''
  });
  const [rules, setRules] = useState<Rules>({
    description: ''
  });
  const [originalRules, setOriginalRules] = useState<Rules>({
    description: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch additional data on component mount
  React.useEffect(() => {
    const fetchAdditionalData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        
        // Fetch point system
        const pointSystemResponse = await fetch(`${apiUrl}/admin/leagues/${league.id}/point-system`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (pointSystemResponse.ok) {
          const pointSystemData = await pointSystemResponse.json();
          setPointSystem(pointSystemData);
          setOriginalPointSystem(pointSystemData); // Store original
        }

        // Fetch rules
        const rulesResponse = await fetch(`${apiUrl}/admin/leagues/${league.id}/rules`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (rulesResponse.ok) {
          const rulesData = await rulesResponse.json();
          setRules(rulesData);
          setOriginalRules(rulesData); // Store original
        }

      } catch (error) {
        console.error('Failed to fetch additional league data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdditionalData();
  }, [league.id]);

  // Helper function to check if basic league data changed
  const hasLeagueDataChanged = (): boolean => {
    return (
      formData.name !== originalFormData.name ||
      formData.description !== originalFormData.description ||
      formData.venue !== originalFormData.venue ||
      formData.city !== originalFormData.city ||
      formData.country !== originalFormData.country ||
      formData.teamFormat !== originalFormData.teamFormat ||
      formData.isActive !== originalFormData.isActive
    );
  };

  // Helper function to check if point system changed
  const hasPointSystemChanged = (): boolean => {
    return (
      pointSystem.points_per_game_win !== originalPointSystem.points_per_game_win ||
      pointSystem.points_per_series_win !== originalPointSystem.points_per_series_win ||
      pointSystem.points_per_both !== originalPointSystem.points_per_both ||
      pointSystem.custom_formula !== originalPointSystem.custom_formula
    );
  };

  // Helper function to check if rules changed
  const hasRulesChanged = (): boolean => {
    return rules.description !== originalRules.description;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      let allSuccessful = true;
      let updatedLeague = formData;
      let changesMade = false;

      // Only save basic league data if it changed (excluding dates to avoid SQL errors)
      if (hasLeagueDataChanged()) {
        changesMade = true;
        const leagueUpdateData = {
          name: formData.name,
          description: formData.description,
          venue: formData.venue,
          city: formData.city,
          country: formData.country,
          teamFormat: formData.teamFormat,
          isActive: formData.isActive
          // Explicitly exclude startDate and endDate
        };

        const leagueResponse = await fetch(`${apiUrl}/admin/leagues/${league.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(leagueUpdateData)
        });

        if (leagueResponse.ok) {
          updatedLeague = await leagueResponse.json();
        } else {
          allSuccessful = false;
          console.error('Failed to update league data');
        }
      }

      // Only save point system if it changed
      if (hasPointSystemChanged()) {
        changesMade = true;
        const pointSystemResponse = await fetch(`${apiUrl}/admin/leagues/${league.id}/point-system`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(pointSystem)
        });

        if (!pointSystemResponse.ok) {
          allSuccessful = false;
          console.error('Failed to update point system');
        }
      }

      // Only save rules if they changed
      if (hasRulesChanged()) {
        changesMade = true;
        const rulesResponse = await fetch(`${apiUrl}/admin/leagues/${league.id}/rules`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(rules)
        });

        if (!rulesResponse.ok) {
          allSuccessful = false;
          console.error('Failed to update rules');
        }
      }

      if (!changesMade) {
        // No changes were made, just close the modal
        onClose();
        return;
      }

      if (allSuccessful) {
        onSave(updatedLeague);
      } else {
        alert('Some updates failed. Please try again.');
      }
    } catch (error) {
      console.error('Failed to update league:', error);
      alert('An error occurred while updating the league');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handlePointSystemChange = (field: string, value: number | boolean | string) => {
    setPointSystem(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRulesChange = (description: string) => {
    setRules({
      description: description
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-xl p-8 border border-blue-400">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-white">{t('admin.leagueDetailsEditor.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-xl p-8 border border-blue-400 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-white">{t('admin.leagueDetailsEditor.title')}</h3>
          <button
            onClick={onClose}
            className="text-white hover:text-blue-300 transition duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                {t('admin.leagueDetailsEditor.leagueName')}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="venue" className="block text-sm font-medium text-white mb-2">
                {t('admin.leagueDetailsEditor.venue')}
              </label>
              <input
                type="text"
                id="venue"
                name="venue"
                value={formData.venue}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="city" className="block text-sm font-medium text-white mb-2">
                {t('admin.leagueDetailsEditor.city')}
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="country" className="block text-sm font-medium text-white mb-2">
                {t('admin.leagueDetailsEditor.country')}
              </label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="teamFormat" className="block text-sm font-medium text-white mb-2">
                {t('admin.leagueDetailsEditor.teamFormat')}
              </label>
              <select
                id="teamFormat"
                name="teamFormat"
                value={formData.teamFormat}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{ colorScheme: 'dark' }}
                required
              >
                <option value="individual" className="bg-gray-800 text-white">{t('admin.leagueDetailsEditor.teamFormats.individual')}</option>
                <option value="duos" className="bg-gray-800 text-white">{t('admin.leagueDetailsEditor.teamFormats.duos')}</option>
                <option value="trios" className="bg-gray-800 text-white">{t('admin.leagueDetailsEditor.teamFormats.trios')}</option>
                <option value="squads" className="bg-gray-800 text-white">{t('admin.leagueDetailsEditor.teamFormats.squads')}</option>
                <option value="fives" className="bg-gray-800 text-white">{t('admin.leagueDetailsEditor.teamFormats.fives')}</option>
              </select>
            </div>

            <div>
              <label htmlFor="isActive" className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="rounded border-white/20 bg-white/10 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-white">{t('admin.leagueDetailsEditor.activeLeague')}</span>
              </label>
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-white mb-2">
              {t('admin.leagueDetailsEditor.description')}
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Point System Section */}
          <div className="bg-white/5 rounded-lg p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4">{t('admin.leagueDetailsEditor.pointSystem')}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('admin.leagueDetailsEditor.pointsPerGameWin')}
                </label>
                <input
                  type="number"
                  min="0"
                  value={pointSystem.points_per_game_win}
                  onChange={(e) => handlePointSystemChange('points_per_game_win', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('admin.leagueDetailsEditor.pointsPerSeriesWin')}
                </label>
                <input
                  type="number"
                  min="0"
                  value={pointSystem.points_per_series_win}
                  onChange={(e) => handlePointSystemChange('points_per_series_win', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={pointSystem.points_per_both}
                  onChange={(e) => handlePointSystemChange('points_per_both', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-white/10 border-white/20 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-white">{t('admin.leagueDetailsEditor.awardBothPoints')}</span>
              </label>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-white mb-2">
                {t('admin.leagueDetailsEditor.customFormula')}
              </label>
              <textarea
                rows={3}
                value={pointSystem.custom_formula || ''}
                onChange={(e) => handlePointSystemChange('custom_formula', e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('admin.leagueDetailsEditor.customFormulaPlaceholder')}
              />
            </div>
          </div>

          {/* Rules Section */}
          <div className="bg-white/5 rounded-lg p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4">{t('admin.leagueDetailsEditor.leagueRules')}</h3>
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {t('admin.leagueDetailsEditor.leagueRulesRegulations')}
              </label>
              <textarea
                rows={8}
                value={rules.description}
                onChange={(e) => handleRulesChange(e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('admin.leagueDetailsEditor.rulesPlaceholder')}
              />
            </div>
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-600 disabled:to-gray-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
            >
              {saving ? t('admin.leagueDetailsEditor.saving') : t('admin.leagueDetailsEditor.saveChanges')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
            >
              {t('admin.leagueDetailsEditor.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeagueDetailsEditor;